import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";

const AnalyticsPage = () => {
  const { userData, organizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [llmUsage, setLlmUsage] = useState(null);
  const [chartmindDaily, setChartmindDaily] = useState([]);

  useEffect(() => {
    if (!organizationId || userData?.role !== "admin") {
      setLoading(false);
      return;
    }

    const monthKey = new Date().toISOString().slice(0, 7);
    const load = async () => {
      setLoading(true);
      try {
        const usageSnap = await db
          .collection("organizations")
          .doc(organizationId)
          .collection("llmUsage")
          .doc(monthKey)
          .get();
        setLlmUsage(usageSnap.exists ? usageSnap.data() : { tokensUsed: 0, monthKey });

        const dailySnap = await db
          .collection("organizations")
          .doc(organizationId)
          .collection("chartmind_daily")
          .orderBy("date", "desc")
          .limit(30)
          .get();
        setChartmindDaily(dailySnap.docs.map((doc) => doc.data()));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [organizationId, userData?.role]);

  if (userData?.role !== "admin") {
    return (
      <Alert severity="warning">Organization analytics are available to org admins only.</Alert>
    );
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Organization analytics"
        subtitle="LLM usage and ChartMind activity for your organization."
      />
      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">LLM tokens this month</Typography>
          <Typography variant="h4">{llmUsage?.tokensUsed ?? 0}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            ChartMind daily sessions (last 30 days)
          </Typography>
          {chartmindDaily.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No ChartMind activity recorded yet.
            </Typography>
          ) : (
            chartmindDaily.map((row) => (
              <Typography key={row.date || row.id} variant="body2">
                {row.date}: {row.sessionsStarted ?? row.sessionCount ?? 0} sessions
              </Typography>
            ))
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default AnalyticsPage;
